import { matchPath, useLocation } from 'react-router-dom'
import Debug from 'debug'
import moize from 'moize'

import { useUsername } from '@jotta/grpc-connect-client/customer'
import type { GetPathParams } from '@jotta/grpc-js-client/files/getPath'
import {
  type FileRouteContext,
  type FileRouteSortBy,
  type FileRouteViewMode,
  StrictFileRouteSchema,
} from '@jotta/types/schemas/FileRouteSchema'
import {
  createPath,
  createRelativePath,
  removeTrailingSlash,
  uriDecodePath,
} from '@jotta/utils/pathUtils'
import { isString } from '@jotta/utils/typeAssertions'

import { sortByCookie, viewModeCookie } from './cookies'
import { getFileRouteParams } from './getFileRouteParams'
import { routeParamsToApiPath } from './routeParamsToApiPath'
import { routeParamsToApiPathBreadCrumb } from './routeParamsToApiPathBreadCrumb'
import { routeParamsToRoutePath } from './routeParamsToRoutePath'
import { getSortLinks } from './sortParamToSortBy'

function parsePath(path: string) {
  const debug = Debug('jotta:files:parseFileRouteParams:parsePath')
  debug('path', path)
  const searchMatch = matchPath(
    '/web/search/:viewMode/:sortBy/:query/*',
    path,
  )?.params
  if (searchMatch) {
    return {
      context: 'search',
      ...searchMatch,
    }
  }

  const params = matchPath('/web/:context/:viewMode/:sortBy/*', path)?.params
  debug('params', params)
  return params
}

/**
 *
 * @param params params object or a pathname to parse into params
 * @returns
 */
export function parseFileRouteParams(data: string, username: string) {
  return parseCachedFileRouteParams(
    data,
    username,
    viewModeCookie.get(),
    sortByCookie.get(),
  )
}

const parseCachedFileRouteParams = moize(
  function parseCachedFileRouteParams(
    data: string,
    username: string,
    _viewMode: FileRouteViewMode,
    _sortBy: FileRouteSortBy,
  ) {
    const debug = Debug('jotta:files:parseFileRouteParams')
    const match = parsePath(data)
    const parsed = StrictFileRouteSchema.safeParse(match)
    const params = parsed.success ? parsed.data : getFileRouteParams(data)

    const { context } = params
    const apiPath = routeParamsToApiPath(params)
    const apiPathBreadcrumb = routeParamsToApiPathBreadCrumb(params)
    let shouldRedirect =
      ['trash', 'shared'].includes(params.context) &&
      removeTrailingSlash(params['*'].toLowerCase()) === 'sync'

    let parsedRoute = routeParamsToRoutePath({
      ...params,
    })
    const inputRoute = parsed.success
      ? parsedRoute
      : isString(data)
        ? removeTrailingSlash(data)
        : String(data)
    if (shouldRedirect) {
      parsedRoute = parsedRoute.pop()
    }
    shouldRedirect = shouldRedirect || inputRoute !== parsedRoute
    if (isString(data) && data.includes('%')) {
      debug('data', data)
      debug('inputRoute', inputRoute)
      debug('parsedRoute', parsedRoute)
      debug('shouldRedirect', shouldRedirect)
    }
    const rootRoute = routeParamsToRoutePath({
      ...params,
      '*': '',
    })
    const depth = apiPathBreadcrumb.length - 1
    const isRoot = apiPathBreadcrumb.length <= 1
    const parentApiPath = apiPathBreadcrumb.at(-2) || apiPath
    const sort = getSortLinks(data)

    const includeDeleted = context === 'trash'
    const getPathParams: GetPathParams = {
      path: apiPath,
      sort: sort.sortDir,
      includeDeleted,
    }
    const virtualBackupFolders: readonly FileRouteContext[] = [
      'photos',
      'contacts',
    ]
    const backupContexts: readonly FileRouteContext[] = [
      'backup',
      'photos',
      'contacts',
    ]
    const isBackup = backupContexts.includes(context)
    const isVirtual = virtualBackupFolders.includes(context)

    return {
      ...params,
      apiPath,
      depth,
      getPathParams,
      includeDeleted,
      isRoot: isRoot && !isVirtual,
      isShared: params.context === 'shared',
      isSync: params.context === 'sync',
      isTrash: params.context === 'trash',
      isTrashOrShared: ['trash', 'shared'].includes(params.context),
      isTrashOrSearch: ['trash', 'search'].includes(params.context),
      isSearch: ['search'].includes(params.context),
      isBackup,
      isVirtual,
      params,
      parentApiPath,
      relativeRoutePath: createRelativePath(params['*']),
      rootRoute,
      routePath: parsedRoute,
      shouldRedirect,
      ...sort,
      uniquePath: createPath(username || 'anonymous', params.context, apiPath),
      username,
    }
  },
  {
    maxSize: 100,
  },
)

export function useParsedFileRouteParams() {
  const location = useLocation()
  const username = useUsername()
  return parseFileRouteParams(uriDecodePath(location.pathname), username)
}

export type FileRouteData = ReturnType<typeof parseFileRouteParams>
